import { useState, useSyncExternalStore } from 'react';
import { useResolutionData } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/[:draftId]/resolution/resolution-loader';
import Button from '~/components/button';
import usePaginate from '~/hooks/usePaginate';
import { useExchangeProducts, useExchangeSelection } from './exchange-options';
import ProductDisplay from './product-display';

const mediaQuery = window.matchMedia('(min-width: 640px)'); // same breakpoint as `sm` prefix in TailwindCSS, with v4 this could use something along the lines of `getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-sm')` for the value
function useResponsiveProductsPerPage() {
  const matches = useSyncExternalStore(
    (onChange) => {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    },
    () => mediaQuery.matches,
  );

  return matches ? 8 : 4;
}

function PaginationButton({
  label,
  onClick,
  disabled = false,
  direction,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  direction: 'previous' | 'next';
}) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      variant="outlined"
      className="gap-1"
    >
      {direction === 'previous' && <span aria-hidden>←</span>}
      {label}
      {direction === 'next' && <span aria-hidden>→</span>}
    </Button>
  );
}

export default function ProductSelectionGrid() {
  const [{ selectedProduct }, dispatch] = useExchangeSelection();
  const { draftClaimLineItem } = useResolutionData();

  const exchangeProducts = useExchangeProducts();
  const [page, setPage] = useState(1);
  const productsPerPage = useResponsiveProductsPerPage();

  const paginatedProducts = usePaginate(exchangeProducts, productsPerPage);
  const totalPages = paginatedProducts.length;
  const currentPage = paginatedProducts.at(page - 1) ?? [];

  const hasPrevious = page > 1;
  const hasNext = page < totalPages;

  if (!exchangeProducts.length) return null;

  if (selectedProduct) return null;

  return (
    <>
      <div
        className="mb-4 flex flex-col gap-2"
        data-testid="productSelectionGrid"
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {/* // TODO consider an aria grid so focus can quickly skip these */}
          {currentPage.map((product) => (
            <ProductDisplay
              key={product.idFromPlatform}
              product={product}
              banner={
                (
                  product.idFromPlatform ===
                  draftClaimLineItem.lineItem.productIdFromPlatform
                ) ?
                  'Current Product'
                : null
              }
              onSelect={() => dispatch({ type: 'setProduct', product })}
            />
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <PaginationButton
            label="Previous"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={!hasPrevious}
            direction="previous"
          />

          <p className="text-center text-sm font-medium text-gray-600">
            {page} of {paginatedProducts.length}
          </p>

          <PaginationButton
            label="Next"
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={!hasNext}
            direction="next"
          />
        </div>
      )}
    </>
  );
}
